// @flow
import * as React from 'react';
import { Redirect, useLocation } from 'react-router-dom';

import { storedSession } from 'common/services/StoredSession';
import { useAuth } from 'common/modules/auth';
import withRouteTracker from 'common/middleware/RouteTracker';
import AppLoginView from './components/AppLoginView';

import Product from 'common/enums/Product';
import { parseUrlClientId } from 'common/utils/URLUtils';
import useResolvedClientId from 'common/hooks/useResolvedClientId';
import { ProviderDataFetcher } from 'common/modules/provider';
import ExternalRedirect from 'common/components/core/router/ExternalRedirect';

const AppLoginContainer = () => {
    const location = useLocation();
    const { providerId } = useResolvedClientId();
    const auth = useAuth();

    const { state: locationState, search } = location;

    const searchParams = new URLSearchParams(search);
    const logoutRedirectUrl = searchParams.get('logoutRedirectUrl');

    /**
     * This redirects to the custom page for SSO enabled clients
     * */
    if (logoutRedirectUrl) {
        return <ExternalRedirect url={logoutRedirectUrl} />;
    }

    if (!auth.isAuthenticated) {
        return (
            <ProviderDataFetcher providerId={providerId}>
                <AppLoginView />
            </ProviderDataFetcher>
        );
    }

    /**
     * This redirects the page to the stored location (if present, & not '/login'),
     * otherwise, to '/' (which handles default redirection)
     * */
    if (!locationState) {
        return <Redirect to="/" />;
    }

    /**
     * if we have a stored sesssion (cookies), or if
     * auth is logged in, redirect to authenticated routes
     */
    const { fromLocation } = locationState;
    const fromLocationClientId = parseUrlClientId(locationState.fromLocation);
    if (
        fromLocation &&
        fromLocation.pathname !== '/login' &&
        storedSession.clientId === fromLocationClientId
    ) {
        return <Redirect to={fromLocation} />;
    }

    return <Redirect to="/" />;
};

export default (withRouteTracker(
    AppLoginContainer,
    Product.PLATFORM
): React.AbstractComponent<{}>);
